import { useState } from "react";
import { UserPlus, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });

/** Calendly bookings whose invitee email matched no student — coach assigns them manually. */
export const UnassignedBookings = ({ bookings, students, onChange }) => {
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(null);

  if (!bookings?.length) return null;

  const assign = async (b) => {
    const userId = picked[b.id];
    if (!userId) {
      toast.error("Pick a student first");
      return;
    }
    setBusy(b.id);
    try {
      const { data } = await api.post(`/admin/unmatched-bookings/${b.id}/assign`, { user_id: userId });
      toast.success(`Added to ${data.assigned_to}'s calendar`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (b) => {
    setBusy(b.id);
    try {
      await api.delete(`/admin/unmatched-bookings/${b.id}`);
      toast.success("Dismissed");
      onChange();
    } catch (e) {
      toast.error("Could not dismiss");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="surface-card p-5 mb-8"
      style={{ borderColor: "var(--border-lime)" }}
      data-testid="unassigned-bookings"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={15} style={{ color: "var(--lime)" }} />
        <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--lime)" }}>
          Needs assigning ({bookings.length})
        </span>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
        These calls were booked with an email that doesn&apos;t match any student account, so they never
        reached a student&apos;s calendar. Pick the right student and the call shows up on their end.
      </p>

      <div className="space-y-2">
        {bookings.map((b) => (
          <div
            key={b.id}
            className="p-4 rounded-2xl flex flex-col sm:flex-row sm:items-center gap-3"
            style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
            data-testid={`unassigned-booking-${b.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{b.invitee_name || b.invitee_email}</div>
              <div className="text-xs font-mono truncate" style={{ color: "var(--text-muted)" }}>
                {b.invitee_email} · {fmt(b.start_time_iso)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={picked[b.id] || ""}
                onChange={(e) => setPicked((p) => ({ ...p, [b.id]: e.target.value }))}
                className="input-base text-sm py-2 min-w-[180px]"
                style={{ background: "var(--surface)", color: "var(--text)" }}
                data-testid={`unassigned-student-select-${b.id}`}
              >
                <option value="">Assign to…</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.email})
                  </option>
                ))}
              </select>
              <button
                onClick={() => assign(b)}
                disabled={busy === b.id}
                className="btn-primary text-sm flex items-center gap-2 whitespace-nowrap"
                data-testid={`unassigned-assign-${b.id}`}
              >
                {busy === b.id ? <span className="spinner" /> : <UserPlus size={14} />} Assign
              </button>
              <button
                onClick={() => dismiss(b)}
                disabled={busy === b.id}
                className="p-2 rounded-xl hover:bg-white/5 transition"
                style={{ color: "var(--text-muted)", border: "1px solid var(--border)" }}
                title="Dismiss"
                data-testid={`unassigned-dismiss-${b.id}`}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UnassignedBookings;
